'use client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSearchParams } from 'next/navigation';

import { ROLES, RoleType } from 'types/roles';

import { AuthForm, FormData } from '@components/AuthForm';
import { StyledLink } from '@components/Common';
import { LoginWrapper } from '@components/Login/styles';
import { TextGroup } from '@components/TextGroups';
import { showToast } from '@components/ToastContainer';

import { login } from '@utils/supabase/auth-actions';

interface ErrorResponse {
  message: string;
  status: number;
}

/**
 * Login component that handles user authentication.
 */
export function Login({ role }: { role: RoleType }) {
  const { t } = useTranslation('common');
  const searchParams = useSearchParams();
  const [hasError, setHasError] = useState(false);

  // Check for subdomain mismatch error on component mount
  useEffect(() => {
    const errorParam = searchParams?.get('error');
    if (errorParam === 'subdomain_mismatch') {
      setHasError(true);
      showToast('error', t('toast.errorSubdomainMismatch'), t('toast.errorSubdomainMismatchDescription'));
    }
  }, [searchParams, t]);

  const handleFormSubmit = async (data: FormData) => {
    const redirectTo = searchParams?.get('redirectTo');
    const decodedRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : undefined;
    const response = await login(data, role, decodedRedirectTo);

    if (!response) {
      // Login successful (no response means redirect happened)
      return;
    }

    if (response.error) {
      const errorData = response.error as ErrorResponse;

      if (errorData.status === 400) {
        setHasError(true);
        showToast('error', t('toast.errorLogin'), t('toast.errorLoginDescription'));
      } else if (errorData.status === 403) {
        setHasError(true);
        showToast(
          'error',
          t('toast.errorAccountDisabled'),
          t('toast.errorAccountDisabledDescription'),
        );
      }
    }
  };

  const getLoginTitle = () => {
    switch (role) {
      case ROLES.ADMIN:
        return t('login.titleAdmin');
      case ROLES.SCHOOL:
        return t('login.titleSchool');
      case ROLES.TEACHER:
        return t('login.titleTeacher');
      default:
        return t('login.title');
    }
  };

  // Render simplified admin login without register/forgot password options
  if (role === ROLES.ADMIN) {
    return (
      <LoginWrapper>
        <TextGroup header={getLoginTitle()} paragraph={t('admin.adminLogin')} />
        <AuthForm
          onSubmit={handleFormSubmit}
          hasError={hasError}
          labelButton={t('login.title')}
        />
      </LoginWrapper>
    );
  }

  // Render regular login for teachers and schools
  return (
    <LoginWrapper>
      <TextGroup
        header={getLoginTitle()}
        paragraph={t('login.description') + ' '}
        href={`/${role}/register`}
        linkLabel={t('login.registerThen')}
      />
      <AuthForm
        onSubmit={handleFormSubmit}
        hasError={hasError}
        labelButton={t('login.title')}
      />
      <StyledLink href={`/${role}/forgot-password`} passHref className="defaultAStyle">
        {t('login.forgotPassword')}
      </StyledLink>
    </LoginWrapper>
  );
}
